import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { authClient } from "../lib/auth-client";
import { getAppBuildInfo } from "../lib/platform";

interface PostHogUserIdentifierProps {
  children: React.ReactNode;
}

export const PostHogUserIdentifier = ({
  children,
}: PostHogUserIdentifierProps) => {
  const posthog = usePostHog();
  const { data: session } = authClient.useSession();

  // Super properties, so app_build rides EVERY event without any per-event
  // plumbing. That breakdown is what answers "which builds are still active"
  // before raising minBuild — the one question you must be able to answer to
  // pull that lever safely. Registered once, outside the session effect, so a
  // sign-out (which calls posthog.reset) cannot strip it: reset clears
  // identity, and re-registering here on the next mount restores it.
  useEffect(() => {
    if (!posthog) return;
    void getAppBuildInfo().then((info) => {
      if (info === null) return;
      posthog.register({ app_build: info.build, app_platform: info.platform });
    });
  }, [posthog]);

  useEffect(() => {
    if (!posthog) return;

    if (session?.user) {
      posthog.identify(session.user.id, {
        email: session.user.email,
        name: session.user.name,
        is_membership_active: session.session.is_membership_active,
        is_admin: session.session.is_admin,
      });
    } else {
      posthog.reset();
    }
  }, [posthog, session]);

  return <>{children}</>;
};
